import { ArrowRight, ClipboardList } from "lucide-react";
import type { TableColumn } from "../../common/Table";
import { SimpleTooltip } from "../../common";
import AssignedToCell from "./components/AssignedToCell";
import type { SurveyResponseSummary } from "./types";
import { formatDDMMYYYY } from "../../../utils/timeUtils";
import {
  responseRate,
  rateColorToken,
  surveyTileToken,
  assignedSummary,
} from "../../../utils/surveyInsights";

export interface SurveyTableHandlers {
  onViewInsights: (survey: SurveyResponseSummary) => void;
}

/** Figma closed-surveys table columns (dc.html:2920): survey / audience /
 *  closed / responses / rate / view link. All values from live API data. */
export function getSurveyTableColumns({
  onViewInsights,
}: SurveyTableHandlers): TableColumn<SurveyResponseSummary>[] {
  return [
    {
      key: "survey",
      title: "Survey",
      label: "Survey",
      required: true,
      render: (_, record) => {
        const tile = surveyTileToken(record.id);
        return (
          <div className="flex items-center gap-[11px]">
            <div
              className={`w-[38px] h-[38px] rounded-[11px] grid place-items-center flex-none ${tile.tileBg}`}
            >
              <ClipboardList className={`w-[18px] h-[18px] ${tile.tileColor}`} />
            </div>
            <div className="min-w-0 flex-1">
              <SimpleTooltip label={record.title} side="top">
                <span className="font-bold text-label text-[var(--color-text-primary)] block truncate">
                  {record.title}
                </span>
              </SimpleTooltip>
              <span className="block text-body-xs text-[var(--color-text-tertiary)] truncate">
                {formatDDMMYYYY(record.startDate) ?? "—"} –{" "}
                {formatDDMMYYYY(record.endDate) ?? "—"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      key: "audience",
      title: "Audience",
      label: "Audience",
      render: (_, record) => (
        <span className="text-body-sm font-medium text-[var(--color-text-strong)]">
          <AssignedToCell surveyId={record.id} assignedTo={record.assignedTo} />
        </span>
      ),
    },
    {
      key: "closed",
      title: "Closed",
      label: "Closed",
      render: (_, record) => (
        <span className="text-body-sm font-medium text-[var(--color-text-strong)] whitespace-nowrap">
          {formatDDMMYYYY(record.endDate) ?? "—"}
        </span>
      ),
    },
    {
      key: "responses",
      title: "Responses",
      label: "Responses",
      render: (_, record) => (
        <span className="text-body-sm font-bold text-[var(--color-text-primary)] whitespace-nowrap">
          {record.completed.toLocaleString()} / {record.total.toLocaleString()}
        </span>
      ),
    },
    {
      key: "rate",
      title: "Response rate",
      label: "Response rate",
      render: (_, record) => {
        const rate = responseRate(record.completed, record.total);
        const color = rateColorToken(rate);
        return (
          <div className="flex items-center gap-[9px] min-w-[170px]">
            <div className="flex-1 h-[7px] rounded-full bg-[var(--color-bg-subtle)] overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-300"
                style={{ width: `${Math.min(rate, 100)}%`, background: color }}
              />
            </div>
            <span className="text-caption font-bold whitespace-nowrap" style={{ color }}>
              {rate}%
            </span>
          </div>
        );
      },
    },
    {
      key: "actions",
      title: "",
      label: "Actions",
      required: true,
      align: "right",
      render: (_, record) => (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onViewInsights(record);
          }}
          aria-label={`View results for ${record.title}`}
          className="inline-flex items-center gap-1.5 text-body-sm font-bold text-[var(--color-primary-700)] hover:text-[var(--color-primary-600)] transition-colors cursor-pointer border-0 bg-transparent p-0 whitespace-nowrap focus-visible:ring-2 focus-visible:ring-[var(--color-primary-500)] focus-visible:outline-none focus-visible:rounded"
        >
          View results
          <ArrowRight className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      ),
    },
  ];
}

export function getAudienceLabel(survey: SurveyResponseSummary): string {
  return assignedSummary(
    survey.assignedTo.entities.map((e) => e.name),
    survey.assignedTo.totalCount
  );
}
